import { useCallback, useRef, type KeyboardEvent as ReactKeyboardEvent, type RefObject, useState } from 'react';
import type { MarketSearchResult } from '../../../services/marketSearchService';
import { formatDivisionReplacement } from '../mentionUtils';
import type { DivisionSearchResult, InsightsMentionsState, UserSearchResult } from '../InsightsPage.types';
import { useInsightsMentionSearches } from './useInsightsMentionSearches';

interface UseInsightsMentionsOptions {
  inputValue: string;
  setInputValue: (nextValue: string) => void;
  inputRef: RefObject<HTMLTextAreaElement | null>;
}

type KeyDownHandlingResult = 'handled' | 'submit' | 'ignored';

interface UseInsightsMentionsResult extends InsightsMentionsState {
  handleInputChange: (nextValue: string, cursor: number) => void;
  handleCursorChange: (currentValue: string, cursor: number) => void;
  handleKeyDown: (event: ReactKeyboardEvent<HTMLTextAreaElement>) => KeyDownHandlingResult;
  closeAllMentionMenus: () => void;
  selectUserResult: (selected: UserSearchResult) => void;
  selectDivisionResult: (selected: DivisionSearchResult) => void;
  selectMarketResult: (selected: MarketSearchResult) => void;
}

export const useInsightsMentions = ({
  inputValue,
  setInputValue,
  inputRef,
}: UseInsightsMentionsOptions): UseInsightsMentionsResult => {
  const inputCursorRef = useRef<number>(0);
  const [userSearchQuery, setUserSearchQuery] = useState<string | null>(null);
  const [userSearchTokenStart, setUserSearchTokenStart] = useState<number | null>(null);
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [isUserSearchLoading, setIsUserSearchLoading] = useState<boolean>(false);
  const [isUserSearchOpen, setIsUserSearchOpen] = useState<boolean>(false);
  const [userSearchActiveIndex, setUserSearchActiveIndex] = useState<number>(0);

  const [divisionSearchQuery, setDivisionSearchQuery] = useState<string | null>(null);
  const [divisionSearchTokenStart, setDivisionSearchTokenStart] = useState<number | null>(null);
  const [divisionSearchResults, setDivisionSearchResults] = useState<DivisionSearchResult[]>([]);
  const [isDivisionSearchLoading, setIsDivisionSearchLoading] = useState<boolean>(false);
  const [isDivisionSearchOpen, setIsDivisionSearchOpen] = useState<boolean>(false);
  const [divisionSearchActiveIndex, setDivisionSearchActiveIndex] = useState<number>(0);

  const [marketSearchQuery, setMarketSearchQuery] = useState<string | null>(null);
  const [marketSearchTokenStart, setMarketSearchTokenStart] = useState<number | null>(null);
  const [marketSearchResults, setMarketSearchResults] = useState<MarketSearchResult[]>([]);
  const [isMarketSearchLoading, setIsMarketSearchLoading] = useState<boolean>(false);
  const [isMarketSearchOpen, setIsMarketSearchOpen] = useState<boolean>(false);
  const [marketSearchActiveIndex, setMarketSearchActiveIndex] = useState<number>(0);

  const closeAllMentionMenus = useCallback((): void => {
    setIsUserSearchOpen(false);
    setIsDivisionSearchOpen(false);
    setIsMarketSearchOpen(false);
  }, []);

  const updateUserSearchContext = useCallback((nextValue: string, cursor: number): void => {
    const prefix = '@user:';
    const beforeCursor = nextValue.slice(0, cursor);
    const atIndex = beforeCursor.lastIndexOf(prefix);
    if (atIndex === -1) {
      setUserSearchQuery(null);
      setUserSearchTokenStart(null);
      setIsUserSearchOpen(false);
      return;
    }

    const tokenStart = atIndex;
    const afterPrefix = beforeCursor.slice(tokenStart + prefix.length);
    if (/\s/.test(afterPrefix)) {
      setUserSearchQuery(null);
      setUserSearchTokenStart(null);
      setIsUserSearchOpen(false);
      return;
    }

    const query = afterPrefix.trim();
    if (!query) {
      setUserSearchQuery(null);
      setUserSearchTokenStart(tokenStart);
      setUserSearchResults([]);
      setIsUserSearchOpen(false);
      return;
    }

    setUserSearchQuery(query);
    setUserSearchTokenStart(tokenStart);
    setIsUserSearchOpen(true);
    setIsDivisionSearchOpen(false);
    setIsMarketSearchOpen(false);
  }, []);

  const updateDivisionSearchContext = useCallback((nextValue: string, cursor: number): void => {
    const prefix = '@division';
    const beforeCursor = nextValue.slice(0, cursor);
    const atIndex = beforeCursor.lastIndexOf(prefix);
    if (atIndex === -1) {
      setDivisionSearchQuery(null);
      setDivisionSearchTokenStart(null);
      setIsDivisionSearchOpen(false);
      return;
    }

    const tokenStart = atIndex;
    let afterPrefix = beforeCursor.slice(tokenStart + prefix.length);
    if (afterPrefix.startsWith(':')) {
      afterPrefix = afterPrefix.slice(1);
    }

    if (/\s/.test(afterPrefix)) {
      setDivisionSearchQuery(null);
      setDivisionSearchTokenStart(null);
      setIsDivisionSearchOpen(false);
      return;
    }

    const query = afterPrefix.trim();
    if (!query) {
      setDivisionSearchQuery(null);
      setDivisionSearchTokenStart(tokenStart);
      setDivisionSearchResults([]);
      setIsDivisionSearchOpen(false);
      return;
    }

    setDivisionSearchQuery(query);
    setDivisionSearchTokenStart(tokenStart);
    setIsDivisionSearchOpen(true);
    setIsUserSearchOpen(false);
    setIsMarketSearchOpen(false);
  }, []);

  const updateMarketSearchContext = useCallback((nextValue: string, cursor: number): void => {
    const prefix = '@market';
    const beforeCursor = nextValue.slice(0, cursor);
    const atIndex = beforeCursor.lastIndexOf(prefix);
    if (atIndex === -1) {
      setMarketSearchQuery(null);
      setMarketSearchTokenStart(null);
      setIsMarketSearchOpen(false);
      return;
    }

    const tokenStart = atIndex;
    let afterPrefix = beforeCursor.slice(tokenStart + prefix.length);
    if (afterPrefix.startsWith(':')) {
      afterPrefix = afterPrefix.slice(1);
    }

    if (/\s/.test(afterPrefix)) {
      setMarketSearchQuery(null);
      setMarketSearchTokenStart(null);
      setIsMarketSearchOpen(false);
      return;
    }

    const query = afterPrefix.trim();
    if (!query) {
      setMarketSearchQuery(null);
      setMarketSearchTokenStart(tokenStart);
      setMarketSearchResults([]);
      setIsMarketSearchOpen(false);
      return;
    }

    setMarketSearchQuery(query);
    setMarketSearchTokenStart(tokenStart);
    setIsMarketSearchOpen(true);
    setIsUserSearchOpen(false);
    setIsDivisionSearchOpen(false);
  }, []);

  const syncMentionContexts = useCallback(
    (nextValue: string, cursor: number): void => {
      updateUserSearchContext(nextValue, cursor);
      updateDivisionSearchContext(nextValue, cursor);
      updateMarketSearchContext(nextValue, cursor);
    },
    [updateDivisionSearchContext, updateMarketSearchContext, updateUserSearchContext],
  );

  const handleInputChange = useCallback(
    (nextValue: string, cursor: number): void => {
      inputCursorRef.current = cursor;
      setInputValue(nextValue);
      syncMentionContexts(nextValue, cursor);
    },
    [setInputValue, syncMentionContexts],
  );

  const handleCursorChange = useCallback(
    (currentValue: string, cursor: number): void => {
      inputCursorRef.current = cursor;
      syncMentionContexts(currentValue, cursor);
    },
    [syncMentionContexts],
  );

  const selectUserResult = useCallback(
    (selected: UserSearchResult): void => {
      const start = userSearchTokenStart;
      const cursor = inputCursorRef.current;
      if (start == null) {
        return;
      }

      const before = inputValue.slice(0, start);
      const after = inputValue.slice(cursor);
      const replacement = `auth_service_uuid=${selected.authServiceUuid}`;
      const nextValue = `${before}${replacement}${after}`;
      const nextCursor = before.length + replacement.length;

      setInputValue(nextValue);
      setIsUserSearchOpen(false);
      setUserSearchQuery(null);
      setUserSearchResults([]);
      setUserSearchActiveIndex(0);

      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(nextCursor, nextCursor);
      });
    },
    [inputRef, inputValue, setInputValue, userSearchTokenStart],
  );

  const selectDivisionResult = useCallback(
    (selected: DivisionSearchResult): void => {
      const start = divisionSearchTokenStart;
      const cursor = inputCursorRef.current;
      if (start == null) {
        return;
      }

      const before = inputValue.slice(0, start);
      const after = inputValue.slice(cursor);
      const replacement = formatDivisionReplacement(selected);
      const nextValue = `${before}${replacement}${after}`;
      const nextCursor = before.length + replacement.length;

      setInputValue(nextValue);
      setIsDivisionSearchOpen(false);
      setDivisionSearchQuery(null);
      setDivisionSearchResults([]);
      setDivisionSearchActiveIndex(0);

      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(nextCursor, nextCursor);
      });
    },
    [divisionSearchTokenStart, inputRef, inputValue, setInputValue],
  );

  const selectMarketResult = useCallback(
    (selected: MarketSearchResult): void => {
      const start = marketSearchTokenStart;
      const cursor = inputCursorRef.current;
      if (start == null) {
        return;
      }

      const before = inputValue.slice(0, start);
      const after = inputValue.slice(cursor);
      const replacement = `"${selected.name}" (marketUuid=${selected.marketUuid})`;
      const nextValue = `${before}${replacement}${after}`;
      const nextCursor = before.length + replacement.length;

      setInputValue(nextValue);
      setIsMarketSearchOpen(false);
      setMarketSearchQuery(null);
      setMarketSearchResults([]);
      setMarketSearchActiveIndex(0);

      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(nextCursor, nextCursor);
      });
    },
    [inputRef, inputValue, marketSearchTokenStart, setInputValue],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLTextAreaElement>): KeyDownHandlingResult => {
      const isUserAutocompleteActive = isUserSearchOpen && userSearchResults.length > 0;
      const isDivisionAutocompleteActive = isDivisionSearchOpen && divisionSearchResults.length > 0;
      const isMarketAutocompleteActive = isMarketSearchOpen && marketSearchResults.length > 0;

      if (event.key === 'Enter' && !event.shiftKey) {
        if (isUserAutocompleteActive) {
          const selected = userSearchResults[userSearchActiveIndex];
          if (selected) {
            event.preventDefault();
            selectUserResult(selected);
          }

          return 'handled';
        }

        if (isDivisionAutocompleteActive) {
          const selected = divisionSearchResults[divisionSearchActiveIndex];
          if (selected) {
            event.preventDefault();
            selectDivisionResult(selected);
          }

          return 'handled';
        }

        if (isMarketAutocompleteActive) {
          const selected = marketSearchResults[marketSearchActiveIndex];
          if (selected) {
            event.preventDefault();
            selectMarketResult(selected);
          }

          return 'handled';
        }

        event.preventDefault();
        return 'submit';
      }

      if (event.key === 'Escape') {
        closeAllMentionMenus();
        return 'handled';
      }

      if (!isUserSearchOpen && !isDivisionSearchOpen && !isMarketSearchOpen) {
        return 'ignored';
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();

        if (isUserAutocompleteActive) {
          setUserSearchActiveIndex((prev) => Math.min(prev + 1, Math.max(0, userSearchResults.length - 1)));
          return 'handled';
        }

        if (isDivisionAutocompleteActive) {
          setDivisionSearchActiveIndex((prev) => Math.min(prev + 1, Math.max(0, divisionSearchResults.length - 1)));
          return 'handled';
        }

        if (isMarketAutocompleteActive) {
          setMarketSearchActiveIndex((prev) => Math.min(prev + 1, Math.max(0, marketSearchResults.length - 1)));
          return 'handled';
        }
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();

        if (isUserAutocompleteActive) {
          setUserSearchActiveIndex((prev) => Math.max(0, prev - 1));
          return 'handled';
        }

        if (isDivisionAutocompleteActive) {
          setDivisionSearchActiveIndex((prev) => Math.max(0, prev - 1));
          return 'handled';
        }

        if (isMarketAutocompleteActive) {
          setMarketSearchActiveIndex((prev) => Math.max(0, prev - 1));
          return 'handled';
        }
      }

      return 'ignored';
    },
    [
      closeAllMentionMenus,
      divisionSearchActiveIndex,
      divisionSearchResults,
      isDivisionSearchOpen,
      isMarketSearchOpen,
      isUserSearchOpen,
      marketSearchActiveIndex,
      marketSearchResults,
      selectDivisionResult,
      selectMarketResult,
      selectUserResult,
      userSearchActiveIndex,
      userSearchResults,
    ],
  );

  useInsightsMentionSearches({
    userSearchQuery,
    isUserSearchOpen,
    setUserSearchResults,
    setIsUserSearchLoading,
    setUserSearchActiveIndex,
    divisionSearchQuery,
    isDivisionSearchOpen,
    setDivisionSearchResults,
    setIsDivisionSearchLoading,
    setDivisionSearchActiveIndex,
    marketSearchQuery,
    isMarketSearchOpen,
    setMarketSearchResults,
    setIsMarketSearchLoading,
    setMarketSearchActiveIndex,
  });

  return {
    userSearchResults,
    isUserSearchLoading,
    isUserSearchOpen,
    userSearchActiveIndex,
    divisionSearchResults,
    isDivisionSearchLoading,
    isDivisionSearchOpen,
    divisionSearchActiveIndex,
    marketSearchResults,
    isMarketSearchLoading,
    isMarketSearchOpen,
    marketSearchActiveIndex,
    handleInputChange,
    handleCursorChange,
    handleKeyDown,
    closeAllMentionMenus,
    selectUserResult,
    selectDivisionResult,
    selectMarketResult,
  };
};
